/* @jsx h */
import { useMenu } from "@app/vue/providers/index";
import { calculateCartSubtotal } from "@lemmonhq/orders";
import { defineComponent, ref, h, computed, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";

export const TipsSection = defineComponent({
  setup() {
    const { cart, shop } = useMenu();
    const { t } = useLocalization();
    const selectedTipType = ref<"percentage" | "custom" | null>(null);
    const selectedPercentage = ref<number>(0);
    const customAmount = ref<string>("");

    const subtotal = computed(() => calculateCartSubtotal(cart.cart));
    const tipOptions = [5, 10, 15, 20];

    const calculatedTip = computed(() => {
      if (
        selectedTipType.value === "percentage" &&
        selectedPercentage.value > 0
      ) {
        return (subtotal.value * selectedPercentage.value) / 100;
      } else if (selectedTipType.value === "custom" && customAmount.value) {
        return parseFloat(customAmount.value) || 0;
      }
      return 0;
    });

    watch(calculatedTip, (newTip) => {
      if (newTip > 0) {
        cart.cart.tips = { value: newTip };
      } else {
        cart.cart.tips = undefined;
      }
    });

    const selectPercentage = (percentage: number) => {
      if (
        selectedTipType.value === "percentage" &&
        selectedPercentage.value === percentage
      ) {
        // Unselect if clicking the same one
        selectedTipType.value = null;
        selectedPercentage.value = 0;
      } else {
        selectedTipType.value = "percentage";
        selectedPercentage.value = percentage;
        customAmount.value = "";
      }
    };

    const selectCustom = () => {
      if (selectedTipType.value === "custom") {
        selectedTipType.value = null;
        customAmount.value = "";
      } else {
        selectedTipType.value = "custom";
        selectedPercentage.value = 0;
      }
    };

    return () => (
      <div class="p-2">
        <h3 class="text-lg font-semibold mb-4">
          {t("customer.checkout.add_tip")}
        </h3>

        <div class="space-y-4">
          {/* Percentage Options */}
          <div class="grid grid-cols-4 gap-2">
            {tipOptions.map((percentage) => (
              <button
                key={percentage}
                class={`btn btn-sm ${
                  selectedTipType.value === "percentage" &&
                  selectedPercentage.value === percentage
                    ? "btn-primary"
                    : "btn-outline"
                }`}
                onClick={() => selectPercentage(percentage)}
              >
                {percentage}%
              </button>
            ))}
          </div>

          {/* Custom Amount */}
          <div class="space-y-2">
            <button
              class={`btn btn-sm btn-block ${
                selectedTipType.value === "custom"
                  ? "btn-primary"
                  : "btn-outline"
              }`}
              onClick={selectCustom}
            >
              {t("customer.checkout.custom_amount")}
            </button>

            {selectedTipType.value === "custom" && (
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium">{shop.value.currency}</span>
                <input
                  v-model={customAmount.value}
                  type="number"
                  step="0.50"
                  min="0"
                  class="input input-bordered input-sm flex-1"
                  placeholder="0.00"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
});
